import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import DisputeDialog from '../../../dialogs/DisputeDialog';
import ResultReviewTimer from '../../../dialogs/ResultReviewTimer';
import useApiControl from '../../../../common/useApiControl';

const PendingResultContent = ({ onClose, refreshKey, round }) => {
  const [review, setReview] = useState(null);
  const [disputeDialogVisible, setDisputeDialogVisible] = useState(false);

  const params = useMemo(() => ({
    event_id: round?.event_id,
    number: round?.number,
  }), [round?.event_id, round?.number]);

  const { executeRequest } = useApiControl({
    action: '/get_round_result_review',
    params,
    method: 'POST',
  });

  const refresh = useCallback(async () => {
    const data = await executeRequest();
    setReview(data);
  }, [executeRequest]);

  useEffect(() => {
    if (!round?.event_id || !round?.number) {
      return;
    }

    refresh().catch((error) => {
      console.error(error);
    });
  }, [round?.event_id, round?.number, refresh, refreshKey]);

  const answers = review?.answers ?? [];
  const resultKey = review?.result !== null && review?.result !== undefined
    ? String(review.result)
    : '';
  const resultLabel = resultKey !== '' ? answers[Number(resultKey)] : '';
  const disputeRemaining = Math.max(0, Number(review?.result_dispute_remaining ?? 0));
  const activeDisputeCount = Array.isArray(review?.active_disputes)
    ? review.active_disputes.length
    : 0;
  const [reviewEnded, setReviewEnded] = useState(false);

  useEffect(() => {
    if (!review?.result_review_until) {
      setReviewEnded(false);
      return;
    }

    setReviewEnded(new Date(review.result_review_until).getTime() <= Date.now());
  }, [review?.result_review_until]);

  useEffect(() => {
    if (!review?.result_review_until) {
      return undefined;
    }

    const reviewEndMs = new Date(review.result_review_until).getTime();
    const delay = reviewEndMs - Date.now();
    if (delay <= 0) {
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      setReviewEnded(true);
      refresh().catch((error) => {
        console.error(error);
      });
    }, delay + 50);

    return () => clearTimeout(timeoutId);
  }, [review?.result_review_until, refresh]);

  const isHeld = review?.review_status === 'held';
  const isNotFormed = review?.result_status === 'not_formed';
  const isCorrectionReview = Number(review?.result_revision_count ?? 0) > 0;
  const isPendingReview = review?.review_status === 'pending';
  const isReviewAccepting = isPendingReview && !reviewEnded && !isNotFormed;
  const invalidationRequestCount = Number(review?.invalidation_request_count ?? 0);
  const latestCorrection = review?.latest_correction;
  const beforeCorrectionLabel = latestCorrection?.before_result !== null && latestCorrection?.before_result !== undefined
    ? answers[Number(latestCorrection.before_result)]
    : null;
  const afterCorrectionLabel = latestCorrection?.after_result !== null && latestCorrection?.after_result !== undefined
    ? answers[Number(latestCorrection.after_result)]
    : null;
  const title = isNotFormed
    ? 'このラウンドは不成立となりました'
    : isHeld
      ? '相談・判断中'
      : isReviewAccepting && !review?.has_disputed
        ? isCorrectionReview ? '訂正結果を確認してください' : '結果を確認してください'
        : isCorrectionReview ? '訂正結果の再確認中' : '結果確認中';
  const description = isNotFormed
    ? review?.result_reason === 'member_invalidation_requested'
      ? '訂正後の結果に対してラウンドの無効希望が基準に達したため、このラウンドは不成立になりました。ベットは全額返却されます。'
      : review?.result_reason === 'result_dispute_unresolved'
      ? '結果について判断が一致しなかったため、このラウンドは不成立になりました。ベットは全額返却されます。'
      : 'このラウンドは不成立になりました。ベットは返却されます。'
    : isHeld
      ? '再確認依頼が基準に達したため、オーナーが結果訂正または不成立を判断しています。'
      : review?.has_disputed
        ? '再確認を依頼しました。\nオーナーの確認をお待ちください。'
        : isReviewAccepting
          ? isCorrectionReview
            ? '訂正結果に対する再確認時間です。\nこのラウンドを成立させることが難しい場合は、ラウンドの無効を希望できます。'
            : '問題なければこのままお待ちください\n気になる点があれば再確認を依頼できます。'
          : '再確認の受付は終了しました。\n結果の確定までお待ちください。';
  const disputeSummary = isCorrectionReview
    ? `無効希望：${invalidationRequestCount}件`
    : isReviewAccepting || review?.has_disputed
      ? `再確認依頼：${activeDisputeCount}件 / 残り${disputeRemaining}回`
      : `再確認依頼：${activeDisputeCount}件`;

  return (
    <>
      <Box
        sx={{
          width: { xs: 'calc(100% - 32px)', sm: 380 },
          maxWidth: '100%',
          mx: { xs: 2, sm: 0 },
          p: 1,
          boxSizing: 'border-box',
        }}
      >
        <Typography variant="h6" sx={{ fontSize: 17, mb: 0.75 }}>
          {title}
        </Typography>
        {isNotFormed && (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            {description}
          </Alert>
        )}
        <Typography variant="caption" color="text.secondary">
          {isCorrectionReview ? '訂正結果' : '仮結果'}
        </Typography>
        <Typography sx={{ fontWeight: 700, mb: 1 }}>
          {resultLabel || '未登録'}
        </Typography>
        {isCorrectionReview && beforeCorrectionLabel && afterCorrectionLabel && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            訂正内容：{beforeCorrectionLabel} から {afterCorrectionLabel} に変更されました。
          </Typography>
        )}
        {!isNotFormed && (
          <Typography sx={{ fontSize: 14, color: isHeld ? 'warning.main' : 'text.secondary', mb: 1, whiteSpace: 'pre-line' }}>
            {description}
          </Typography>
        )}
        {isPendingReview && !isNotFormed && (
          <ResultReviewTimer
            reviewUntil={review?.result_review_until}
            totalSeconds={review?.result_review_seconds}
          />
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 1.5 }}>
          {disputeSummary}
        </Typography>
        {Array.isArray(review?.active_disputes) && review.active_disputes.length > 0 && (
          <Box sx={{ mb: 1.5 }}>
            {review.active_disputes.map((dispute) => (
              <Typography key={`${dispute.user_id}-${dispute.created_at}`} variant="body2" color="text.secondary">
                {dispute.name}: {dispute.dispute_type === 'round_invalidation_request'
                  ? 'ラウンドの無効を希望'
                  : answers[Number(dispute.claimed_result)] ?? '不明'}
              </Typography>
            ))}
          </Box>
        )}
        {!review?.has_disputed && isReviewAccepting && (
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            {onClose && (
              <Button variant="contained" onClick={onClose}>
                結果を確認しました
              </Button>
            )}
            <Button
              variant="outlined"
              onClick={() => setDisputeDialogVisible(true)}
              disabled={!review || disputeRemaining <= 0}
            >
              再確認を依頼
            </Button>
          </Box>
        )}
      </Box>
      <DisputeDialog
        isVisible={disputeDialogVisible}
        onClose={() => setDisputeDialogVisible(false)}
        review={review}
        onSubmitted={refresh}
      />
    </>
  );
};

export default PendingResultContent;
